import * as React from "react";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { Spinner } from "@/components/common";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-muted/40 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2">
          <div className="grid size-9 place-items-center rounded-lg bg-primary font-bold text-primary-foreground">
            P
          </div>
          <span className="text-lg font-semibold">Poji Yönetim</span>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Giriş hatasının nedeni. Yanlış e-posta ile yanlış şifre ayrılmaz (hesap var mı yok mu sızmasın);
 * ama bağlantı/yapılandırma ve doğrulanmamış e-posta ayrı söylenir, yoksa sorun bulunamaz.
 */
export function loginErrorText(e: unknown): string {
  const err = e as { code?: string; status?: number; message?: string; name?: string };
  if (err?.code === "invalid_credentials") return "E-posta ya da şifre hatalı.";
  if (err?.code === "email_not_confirmed")
    return "E-posta adresi doğrulanmamış. Supabase Studio'da kullanıcıyı açıp “Confirm email” yapın.";
  if (err?.code === "over_request_rate_limit" || err?.status === 429)
    return "Çok fazla deneme. Biraz bekleyip tekrar deneyin.";
  if (err?.name === "AuthRetryableFetchError" || err instanceof TypeError || err?.status === 0)
    return "Giriş sunucusuna (Supabase) ulaşılamıyor. Supabase çalışıyor mu, .env adresi doğru mu?";
  if (err?.status === 401 || /api key|apikey/i.test(err?.message ?? ""))
    return "Supabase anahtarı geçersiz: apps/admin/.env → VITE_SUPABASE_ANON_KEY (Publishable) kontrol edin, sonra pnpm admin:dev'i yeniden başlatın.";
  return `Giriş yapılamadı: ${err?.message ?? "bilinmeyen hata"}`;
}

export function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [forgot, setForgot] = React.useState(false);
  if (forgot) return <ForgotPassword initialEmail={email} onBack={() => setForgot(false)} />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch (e) {
      setError(loginErrorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Centered>
      <Card>
        <CardHeader>
          <CardTitle>Giriş</CardTitle>
          <CardDescription>Yalnızca Poji ekibi içindir.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4" onSubmit={submit}>
            <div className="grid gap-2">
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Şifre</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy}>
              {busy && <Spinner />}
              Giriş yap
            </Button>
            <Button type="button" variant="link" size="sm" onClick={() => setForgot(true)}>
              Şifremi unuttum
            </Button>
          </form>
        </CardContent>
      </Card>
    </Centered>
  );
}

function ForgotPassword({ initialEmail, onBack }: { initialEmail: string; onBack: () => void }) {
  const { sendPasswordReset } = useAuth();
  const [email, setEmail] = React.useState(initialEmail);
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await sendPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(loginErrorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Centered>
      <Card>
        <CardHeader>
          <CardTitle>Şifre sıfırlama</CardTitle>
          <CardDescription>
            {sent
              ? "Bu adreste bir hesap varsa şifre belirleme bağlantısı gönderildi. E-postanızı kontrol edin."
              : "E-posta adresinize şifre belirleme bağlantısı gönderilir."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!sent && (
            <form className="grid gap-4" onSubmit={submit}>
              <div className="grid gap-2">
                <Label htmlFor="reset-email">E-posta</Label>
                <Input
                  id="reset-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button type="submit" disabled={busy}>
                {busy && <Spinner />}
                Bağlantı gönder
              </Button>
            </form>
          )}
          <Button variant="link" size="sm" className="mt-2 w-full" onClick={onBack}>
            Girişe dön
          </Button>
        </CardContent>
      </Card>
    </Centered>
  );
}

/** Yönetici şifresi en az 12 karakter (Supabase'in genel alt sınırından sıkı; 2FA ayrıca zorunlu) */
export const MIN_STAFF_PASSWORD = 12;

/** Davet ya da şifre sıfırlama bağlantısından gelen kişi: yeni şifre (sonra 2FA kurulumu) */
export function SetPasswordPage() {
  const { setPassword, signOut, state } = useAuth();
  const [pw, setPw] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const tooShort = pw.length > 0 && pw.length < MIN_STAFF_PASSWORD;
  const mismatch = pw2.length > 0 && pw !== pw2;
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (pw.length < MIN_STAFF_PASSWORD || pw !== pw2) return;
    setBusy(true);
    setError(null);
    try {
      await setPassword(pw);
    } catch (err) {
      const x = err as { code?: string; message?: string };
      setError(
        x.code === "weak_password"
          ? "Şifre çok zayıf. Daha uzun ve tahmin edilmesi zor bir şifre seçin."
          : x.code === "same_password"
            ? "Yeni şifre eskisiyle aynı olamaz."
            : `Şifre kaydedilemedi: ${x.message ?? "bilinmeyen hata"}`,
      );
      setBusy(false);
    }
  }
  return (
    <Centered>
      <Card>
        <CardHeader>
          <CardTitle>Şifrenizi belirleyin</CardTitle>
          <CardDescription>
            {state.kind === "set_password" && state.email ? `${state.email} · ` : ""}
            En az {MIN_STAFF_PASSWORD} karakter. Sonraki adımda iki adımlı doğrulamayı kuracaksınız.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4" onSubmit={submit}>
            <div className="grid gap-2">
              <Label htmlFor="new-password">Yeni şifre</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                value={pw}
                aria-invalid={tooShort}
                onChange={(e) => setPw(e.target.value)}
              />
              {tooShort && (
                <p className="text-xs text-destructive">En az {MIN_STAFF_PASSWORD} karakter.</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-password2">Yeni şifre (tekrar)</Label>
              <Input
                id="new-password2"
                type="password"
                autoComplete="new-password"
                required
                value={pw2}
                aria-invalid={mismatch}
                onChange={(e) => setPw2(e.target.value)}
              />
              {mismatch && <p className="text-xs text-destructive">Şifreler aynı değil.</p>}
            </div>
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy || pw.length < MIN_STAFF_PASSWORD || pw !== pw2}>
              {busy && <Spinner />}
              Şifreyi kaydet
            </Button>
            <Button type="button" variant="link" size="sm" onClick={() => void signOut()}>
              Vazgeç ve çıkış yap
            </Button>
          </form>
        </CardContent>
      </Card>
    </Centered>
  );
}

/**
 * İki adımlı doğrulama (K-092): yönetici hesabında zorunlu. Doğrulayıcı uygulaması yoksa kurulur
 * (QR kod), varsa 6 haneli kod istenir. Başarılı olunca oturum aal2'ye yükselir.
 */
export function MfaPage() {
  const { refresh, signOut, state } = useAuth();
  const [factorId, setFactorId] = React.useState<string | null>(null);
  const [qr, setQr] = React.useState<string | null>(null);
  const [secret, setSecret] = React.useState<string | null>(null);
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const mfa = supabase().auth.mfa;
      const { data, error: e } = await mfa.listFactors();
      if (cancelled) return;
      if (e) {
        setError(`Doğrulama bilgisi alınamadı: ${e.message}`);
        setLoading(false);
        return;
      }
      const verified = data.totp.find((f) => f.status === "verified");
      if (verified) {
        setFactorId(verified.id);
      } else {
        // yarım kalmış kurulumları temizle, yenisini başlat
        for (const f of data.all.filter((x) => x.status === "unverified"))
          await mfa.unenroll({ factorId: f.id });
        const en = await mfa.enroll({ factorType: "totp", friendlyName: `Poji ${Date.now()}` });
        if (cancelled) return;
        if (en.error)
          setError(
            /disabled/i.test(en.error.message)
              ? "Supabase'te iki adımlı doğrulama (TOTP) kapalı. Local: pnpm exec supabase stop && pnpm exec supabase start; canlı: Supabase paneli → Authentication → MFA."
              : `Doğrulayıcı kurulumu başlatılamadı: ${en.error.message}`,
          );
        else {
          setFactorId(en.data.id);
          setQr(en.data.totp.qr_code);
          setSecret(en.data.totp.secret);
        }
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    if (!factorId) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase().auth.mfa.challengeAndVerify({
      factorId,
      code: code.replace(/\s/g, ""),
    });
    if (err) {
      setError(
        /disabled/i.test(err.message)
          ? "Supabase'te iki adımlı doğrulama (TOTP) kapalı."
          : "Kod doğrulanamadı. Uygulamadaki güncel kodu girin.",
      );
      setBusy(false);
      return;
    }
    await refresh();
    setBusy(false);
  }

  return (
    <Centered>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-primary" />
            İki adımlı doğrulama
          </CardTitle>
          <CardDescription>
            {qr
              ? "Google Authenticator, 1Password gibi bir uygulamayla QR kodu okutun, sonra 6 haneli kodu girin."
              : "Doğrulayıcı uygulamanızdaki 6 haneli kodu girin."}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {loading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : (
            <>
              {qr && (
                <div className="grid justify-items-center gap-2">
                  <img
                    src={qr}
                    alt="Doğrulayıcı QR kodu"
                    className="size-44 rounded-md bg-white p-2"
                  />
                  {secret && (
                    <p className="text-center text-xs text-muted-foreground">
                      Okutamıyorsanız anahtar: <span className="font-mono">{secret}</span>
                    </p>
                  )}
                </div>
              )}
              <form className="grid gap-3" onSubmit={verify}>
                <Label htmlFor="code">Kod</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9 ]{6,7}"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                {error && (
                  <p role="alert" className="text-sm text-destructive">
                    {error}
                  </p>
                )}
                <Button type="submit" disabled={busy || !factorId}>
                  {busy && <Spinner />}
                  Doğrula
                </Button>
              </form>
            </>
          )}
          <Button variant="link" size="sm" onClick={() => void signOut()}>
            {state.kind === "needs_mfa" && state.email ? `${state.email} — çıkış yap` : "Çıkış yap"}
          </Button>
        </CardContent>
      </Card>
    </Centered>
  );
}

export function NotStaffPage() {
  const { signOut, state } = useAuth();
  return (
    <Centered>
      <Card>
        <CardHeader>
          <CardTitle>Erişim yok</CardTitle>
          <CardDescription>
            {state.kind === "not_staff" && state.email ? `${state.email} ` : ""}
            hesabı Poji ekibinde tanımlı değil. Bir yöneticiden davet isteyin.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" className="w-full" onClick={() => void signOut()}>
            Başka hesapla giriş yap
          </Button>
        </CardContent>
      </Card>
    </Centered>
  );
}

export function FullPageSpinner() {
  return (
    <div className="grid min-h-dvh place-items-center">
      <Spinner className="size-6 text-muted-foreground" />
    </div>
  );
}
